import Chart from 'chart.js/auto';
import ChartDataLabels from 'chartjs-plugin-datalabels';

Chart.register(ChartDataLabels);

if (document.querySelector(".dashboard")) {
    // Leaving the page aborts the in-flight dashboard requests ("TypeError: Failed to
    // fetch"); that is not an error worth logging, only a real failure is.
    let dashboardUnloading = false;
    window.addEventListener("beforeunload", () => { dashboardUnloading = true; });
    const logFetchError = (label, error) => {
        if (!dashboardUnloading && error?.name !== "AbortError") {
            console.error(label, error);
        }
    };

    // ==============================
    // 🔹 CHART LOOK (presentational only: brand type + palette)
    // ==============================
    // Colours are read from the CSS tokens (theme/_tokens.scss and the --dash-stage-*
    // palette in screens/_dashboard.scss); the literals are only fallbacks.
    const dashCss = getComputedStyle(document.querySelector(".dashboard"));
    const cssVar = (name, fallback) => (dashCss.getPropertyValue(name) || "").trim() || fallback;
    const FX_INK = cssVar("--fx-ink", "#1B1915");
    const FX_INK_2 = cssVar("--fx-ink-2", "#48433B");
    const FX_ACCENT = cssVar("--fx-accent", "#7A6018");
    const FX_ACCENT_RGB = cssVar("--fx-accent-rgb", "193, 163, 77");
    const FX_NAVY_RGB = cssVar("--fx-navy-rgb", "23, 21, 18");
    // Text on a gold / burnt-orange fill (white on gold is only 2.44:1)
    const FX_ON_GOLD = cssVar("--fx-on-gold", "#17140C");
    // Edge for the light stage slices / legend swatches (champagne and gold are
    // 1.4-2.3:1 on the card on their own; gold-700 is 4.3:1)
    const FX_STAGE_EDGE = cssVar("--fx-stage-edge", "#8C7130");
    Chart.defaults.font.family = cssVar("--fx-font-sans", '"Inter", system-ui, -apple-system, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif');
    Chart.defaults.font.size = 12;
    Chart.defaults.color = cssVar("--fx-ink-3", "#625C52");
    Chart.defaults.plugins.tooltip.backgroundColor = cssVar("--fx-navy-900", "#0E0D0B");
    Chart.defaults.plugins.tooltip.padding = 10;
    Chart.defaults.plugins.tooltip.cornerRadius = 10;
    Chart.defaults.plugins.tooltip.boxPadding = 4;

    // Sales stages in display order, keyed by the enum value that
    // /dashboard/get-sales-stages returns (it leaves out stages with no contacts,
    // so slices are matched by key, never by position). Same order as
    // orderedStages below; colours mirror the board's column dots.
    const STAGES = [
        { key: "prospect",         color: cssVar("--dash-stage-prospect", "#E6CFA8"),    ink: FX_INK,     edge: FX_STAGE_EDGE },
        { key: "qualified",        color: cssVar("--dash-stage-qualified", "#C1A34D"),   ink: FX_ON_GOLD, edge: FX_STAGE_EDGE },
        { key: "short_stack",      color: cssVar("--dash-stage-short-stack", "#2B2B2B"), ink: "#fff" },
        { key: "won",              color: cssVar("--dash-stage-won", "#16805F"),         ink: "#fff" },
        { key: "delayed_decision", color: cssVar("--dash-stage-delayed", "#C86705"),     ink: FX_ON_GOLD },
        { key: "lost",             color: cssVar("--dash-stage-lost", "#A82E34"),        ink: "#fff" },
    ];
    // Generic categorical palette (Bitly referrers) + readable label ink per slice.
    // Light and dark hues alternate so neighbouring slices separate; the green and
    // red come last because they mean Won / Lost elsewhere on the page.
    const SERIES = [
        { color: cssVar("--dash-stage-short-stack", "#2B2B2B"), ink: "#fff" },
        { color: cssVar("--dash-stage-prospect", "#E6CFA8"),    ink: FX_INK },
        { color: cssVar("--dash-stage-delayed", "#C86705"),     ink: FX_ON_GOLD },
        { color: cssVar("--dash-stage-qualified", "#C1A34D"),   ink: FX_ON_GOLD },
        { color: cssVar("--fx-navy-300", "#ACA595"),            ink: FX_INK },
        { color: cssVar("--fx-info", "#2C6488"),                ink: "#fff" },
        { color: cssVar("--fx-navy-200", "#D4CEC0"),            ink: FX_INK },
        { color: "#9DBA8F",                                     ink: FX_INK },  // warm sage
        { color: cssVar("--dash-stage-won", "#16805F"),         ink: "#fff" },
        { color: cssVar("--dash-stage-lost", "#A82E34"),        ink: "#fff" },
    ];
    const legendBottom = {
        display: true,
        position: "bottom",
        labels: { usePointStyle: true, pointStyle: "circle", boxWidth: 8, boxHeight: 8, padding: 10, color: FX_INK_2, font: { size: 12, weight: "500" } }
    };
    // Donut legends sit under the ring in narrow boxes and beside it once the box
    // is wide enough, so the ring keeps its size (re-evaluated on every resize).
    const legendPositionFor = width => (width >= 420 ? "right" : "bottom");
    const donutLegend = (canvas, labels = {}) => ({
        ...legendBottom,
        position: legendPositionFor(canvas.parentElement ? canvas.parentElement.clientWidth : 0),
        labels: { ...legendBottom.labels, ...labels },
    });
    const fitDonutLegend = (chart, size) => {
        // runs inside Chart.js' resize, right before its own update('resize')
        chart.options.plugins.legend.position = legendPositionFor(size.width);
    };

    // ==============================
    // 🔹 FIX CHART DISPLAY
    // ==============================
    var salesChartInstance = null; // Store chart instance
    var salesDonutChartElement = document.getElementById("salesDonutChart");

    if (salesDonutChartElement) {
        var salesDonutChartCtx = salesDonutChartElement.getContext("2d");
        var salesDonutBox = salesDonutChartElement.closest(".dash-chart"); // shows the empty placeholder

        fetch(`/dashboard/get-sales-stages${location.search}`)
            .then(response => response.json())
            .then(data => {
                console.log("✅ Fetched Chart Data:", data); // Debugging log

                if (!data || data.length === 0) {
                    // No contacts yet (empty database): the card shows its empty state
                    if (salesDonutBox) salesDonutBox.classList.add("is-empty");
                    return;
                }

                // One count per stage in STAGES order; stages the endpoint left out are 0
                let values = STAGES.map(stage => {
                    const row = data.find(item => item.sales_stage === stage.key);
                    return row ? Number(row.contact_count) || 0 : 0; // Ensure numerical values
                });

                // 🔹 Define the **minimum visual size** (adjustable)
                const MIN_VISUAL_SIZE = 5; // Minimum percentage visibility
                const totalOriginal = values.reduce((sum, v) => sum + v, 0);

                if (totalOriginal === 0) {
                    if (salesDonutBox) salesDonutBox.classList.add("is-empty");
                    console.warn("⚠️ No valid data for chart.");
                    return;
                }

                // 🔹 Convert values to percentages
                let percentages = values.map(value => (value / totalOriginal) * 100);

                // 🔥 Ensure every non-empty section has **at least** `MIN_VISUAL_SIZE` (empty stages stay 0)
                let boostedPercentages = percentages.map(pct => (pct > 0 && pct < MIN_VISUAL_SIZE ? MIN_VISUAL_SIZE : pct));

                // 🔹 Adjust total so it sums to 100%
                const boostedTotal = boostedPercentages.reduce((sum, p) => sum + p, 0);
                let finalPercentages = boostedPercentages.map(pct => (pct / boostedTotal) * 100);

                // 🔹 Convert back to absolute values for Chart.js (purely visual)
                let adjustedValues = finalPercentages.map(pct => (pct / 100) * totalOriginal);

                console.log("🎯 Adjusted Values (Visual Only):", adjustedValues);

                // 🔥 Destroy existing chart before creating a new one
                if (salesChartInstance) {
                    salesChartInstance.destroy();
                }

                // Now Load in Sales Pipeline //

                fetch(`/dashboard/get-sales-pipeline${location.search}`)
                    .then(response => response.json())
                    .then(data => {
                        if (!data || data.error) {
                            console.error("❌ Error loading sales pipeline data:", data.error);
                            return;
                        }

                        // 🔹 Define the correct sales stage order
                        let orderedStages = ["Prospect", "Qualified", "Short Stack", "Won", "Delayed Decision", "Lost"];

                        // 🔹 Ensure labels & values are ordered properly
                        let labels = orderedStages;
                        let salesStageValues = orderedStages.map(stage => {
                            let index = data.labels ? data.labels.indexOf(stage) : -1;
                            return index !== -1 ? (data.sales_stage_values[index] ?? 0) : 0;
                        });

                        let salesStagePercentages = orderedStages.map(stage => {
                            let index = data.labels ? data.labels.indexOf(stage) : -1;
                            return index !== -1 ? (data.sales_stage_percentages[index] ?? 0) : 0;
                        });

                        // 🔥 If labels or data are missing, retry fetching
                        if (!data.labels || labels.length === 0 || salesStageValues.length === 0) {
                            if (attempts > 0) {
                                console.warn(`⚠️ Retrying chart data fetch... Attempts left: ${attempts}`);
                                setTimeout(() => loadChartData(attempts - 1), 500);
                            } else {
                                console.error("❌ Final attempt failed: No valid data for chart.");
                            }
                            return;
                        }


                        // ✅ Create new Chart with VISUAL adjustments (Data remains intact)
                        salesChartInstance = new Chart(salesDonutChartCtx, {
                            type: "doughnut",
                            data: {
                                labels: labels,
                                datasets: [{
                                    data: adjustedValues, // 🚀 Visually adjusted data
                                    backgroundColor: STAGES.map(stage => stage.color),
                                    // white separators; the light slices get a gold-700 rim
                                    // inside their own edge (the legend swatch inherits it)
                                    borderColor: STAGES.map(stage => stage.edge || "rgba(255, 255, 255, 0.9)"),
                                    borderWidth: STAGES.map(stage => (stage.edge ? 1.5 : 2)),
                                    borderAlign: STAGES.map(stage => (stage.edge ? "inner" : "center")),
                                    borderRadius: 3,
                                    hoverOffset: 6,
                                }]
                            },
                            options: {
                                responsive: true,
                                maintainAspectRatio: false,
                                cutout: "60%",
                                layout: { padding: 6 },
                                onResize: fitDonutLegend,
                                plugins: {
                                    // only stages that have a slice get a legend entry
                                    legend: donutLegend(salesDonutChartElement, {
                                        filter: (item, chartData) => Number(chartData.datasets[0].data[item.index]) > 0
                                    }),
                                    tooltip: {
                                        enabled: true,
                                        callbacks: {
                                            label: function (tooltipItem) {
                                                let index = tooltipItem.dataIndex;
                                                return  [
                                                    `${labels[index]}: ${values[index]} Contacts`,
                                                    `Total Value ($): $${salesStageValues[index].toLocaleString()}`
                                                ]; // ✅ Return an array to create multiple lines
                                            }
                                        }
                                    },
                                    datalabels: {
                                        color: (context) => STAGES[context.dataIndex].ink,
                                        display: (context) => values[context.dataIndex] > 0, // no "0" on an empty stage
                                        font: { weight: "600", size: 12 },
                                        padding: { top: 2 }, // Adds 2px top padding
                                        formatter: (value, context) => values[context.dataIndex] // 🔥 Show correct original value inside chart
                                    }
                                }
                            },
                            plugins: [ChartDataLabels] // Ensure the plugin is loaded
                        });

                    });

            })
            .catch(error => logFetchError("⚠️ Error fetching chart data:", error));
    }
    // (no donut canvas: the cards are hidden for this view, e.g. a sales manager on "Not attached")

    // ==============================
    // 🔹 FETCH DASHBOARD METRICS (Revenues, etc.)
    // ==============================
    // The metric cards are not rendered when the view hides them (a sales manager on
    // "Not attached"), so only fetch and fill them when they are on the page.
    if (document.getElementById("sales-ytd-card")) {
        const setCardText = (id, text) => {
            const card = document.getElementById(id);
            if (card) card.innerText = text;
        };

        axios.get(`/dashboard/get-dashboard-metrics${location.search}`)
            .then(response => {
                const data = response.data;
                console.log("✅ Fetched Dashboard Data:", data); // Debugging log

                if (data.error) {
                    console.error("⚠️ Error fetching revenue data:", data.error);
                    return;
                }

                setCardText("sales-ytd-card", `$${data.sales_ytd}`);
                setCardText("sales-mtd-card", `$${data.sales_mtd}`);
                setCardText("quotes-mtd-card", `${data.quotes_mtd}`);
                setCardText("win-ytd-card", `${data.win_ytd}%`);
                document.querySelector(".dashboard-row-second")?.classList.add("is-loaded"); // drops the loading look
            })
            .catch(error => logFetchError("⚠️ Error fetching dashboard metrics:", error));
    }

    function loadChartData(attempts = 5) {
        fetch(`/dashboard/get-sales-pipeline${location.search}`)
            .then(response => response.json())
            .then(data => {
                console.log("✅ Sales Pipeline Chart Data:", data);

                if (!data || data.error) {
                    console.error("❌ Error loading sales pipeline data:", data.error);
                    return;
                }

                // 🔹 Define the correct sales stage order
                let orderedStages = ["Prospect", "Qualified", "Short Stack", "Won", "Delayed Decision", "Lost"];

                // 🔹 Ensure labels & values are ordered properly
                let labels = orderedStages;
                let salesStageValues = orderedStages.map(stage => {
                    let index = data.labels ? data.labels.indexOf(stage) : -1;
                    return index !== -1 ? (data.sales_stage_values[index] ?? 0) : 0;
                });

                let salesStagePercentages = orderedStages.map(stage => {
                    let index = data.labels ? data.labels.indexOf(stage) : -1;
                    return index !== -1 ? (data.sales_stage_percentages[index] ?? 0) : 0;
                });

                // 🔥 If labels or data are missing, retry fetching
                if (!data.labels || labels.length === 0 || salesStageValues.length === 0) {
                    if (attempts > 0) {
                        console.warn(`⚠️ Retrying chart data fetch... Attempts left: ${attempts}`);
                        setTimeout(() => loadChartData(attempts - 1), 500);
                    } else {
                        console.error("❌ Final attempt failed: No valid data for chart.");
                    }
                    return;
                }

                // 🔥 Destroy previous chart instance before creating a new one
                if (window.salesPipelineChartInstance) {
                    try {
                        window.salesPipelineChartInstance.destroy();
                    } catch (e) {
                        console.warn("⚠️ Chart destroy failed:", e);
                    }
                }
            })
            .catch(error => logFetchError("❌ Error fetching sales pipeline data:", error));
    }

    // ✅ Load chart data initially
    loadChartData();

    const bitlyDailyClicksWidget = document.querySelector('.bitly-daily-clicks-widget');
    if (document.querySelector(".bitly-daily-clicks-widget")) {
        let currentChart = null;
        async function loadBitlyDailyClicks(widget, bitlink){
            const base = widget.dataset.endpoint;
            if (!base || !bitlink) return;
            const url = `${base}?bitlink=${encodeURIComponent(bitlink)}`;
            const metaEl = widget.querySelector('.bitly-meta');
            const canvas = document.getElementById('bitlyDailyClicksChart');

            if (metaEl) metaEl.textContent = 'Loading...';

            try {
                const res = await fetch(url, { headers: { 'X-Requested-With': 'XMLHttpRequest' } });
                if (!res.ok) throw new Error('HTTP ' + res.status);
                const json = await res.json();
                if (json.error) throw new Error(json.error);

                metaEl.textContent = `Total: ${json.total}`;
                canvas.closest('.dash-chart')?.classList.toggle('is-empty', !Number(json.total)); // empty placeholder

                const ctx = canvas.getContext('2d');
                const gradient = ctx.createLinearGradient(0,0,0,180);
                gradient.addColorStop(0,`rgba(${FX_ACCENT_RGB}, 0.22)`);
                gradient.addColorStop(1,`rgba(${FX_ACCENT_RGB}, 0)`);

                if (currentChart) currentChart.destroy();

                const maxY = Math.max(...json.values);
                const paddedMax = Math.ceil(maxY);

                currentChart = new Chart(ctx, {
                    type: 'line',
                    data: {
                        labels: json.labels,
                        datasets: [{
                            label: 'Clicks',
                            data: json.values,
                            fill: true,
                            backgroundColor: gradient,
                            borderColor: FX_ACCENT,
                            borderWidth: 2,
                            pointRadius: 0,
                            pointHoverRadius: 4,
                            pointBackgroundColor: FX_ACCENT,
                            tension: 0.25
                        }]
                    },
                    options: {
                        responsive: true,
                        maintainAspectRatio: false,
                        interaction: { mode: 'index', intersect: false },
                        scales: {
                            x: { ticks: { maxRotation: 0, autoSkip: true }, grid: { display: false }, border: { display: false } },
                            y: {
                                beginAtZero: true,
                                suggestedMax: paddedMax,
                                ticks: {
                                    callback: v => Math.ceil(v), // ceil each tick
                                    precision: 0,
                                    stepSize: Math.ceil(paddedMax / 5) // optional: control number of ticks
                                },
                                grid: { color: `rgba(${FX_NAVY_RGB}, 0.06)` },
                                border: { display: false }
                            }
                        },
                        plugins: {
                            tooltip: { callbacks: { label: c => `${c.parsed.y} clicks` } },
                            legend: { display: false },
                            datalabels: { display: false } // no number on every point
                        }
                    }
                });
            } catch (e) {
                if (metaEl) metaEl.textContent = 'Error';
                console.error('Bitly daily clicks load failed:', e);
            }
        }

        const select = bitlyDailyClicksWidget.querySelector('.bitly-link-select');
        if (select) {
            loadBitlyDailyClicks(bitlyDailyClicksWidget, select.value);
            select.addEventListener('change', () => loadBitlyDailyClicks(bitlyDailyClicksWidget, select.value));
        }
    }

    const bitlyReferrersPeriodWidget = document.querySelector('.bitly-referrers-period-widget');
    if (bitlyReferrersPeriodWidget) {
        let referrersChart = null;

        // JavaScript
        async function loadBitlyReferrersPeriod(widget, bitlink, period) {
            const base = widget.dataset.endpoint;
            if (!base) {
                console.warn('Missing endpoint data-endpoint on widget.');
                return;
            }

            const url = `${base}?bitlink=${encodeURIComponent(bitlink)}&period=${period}`; // <- change back if needed
            const metaEl = widget.querySelector('.bitly-meta');
            const canvas = document.getElementById('bitlyReferrersPeriodChart');
            if (!canvas) {
                console.error('Canvas #bitlyReferrersPeriodChart not found.');
                return;
            }
            if (metaEl) metaEl.textContent = 'Loading...';

            try {
                const res = await fetch(url, { headers: { 'X-Requested-With': 'XMLHttpRequest' } });
                if (!res.ok) throw new Error('HTTP ' + res.status);
                const json = await res.json();
                if (json.error) throw new Error(json.error);

                let labels = json.labels;
                let values = json.values;
                const total = json.total;

                if (metaEl) metaEl.textContent = `Total: ${total}`;
                canvas.closest('.dash-chart')?.classList.toggle('is-empty', !Number(total)); // empty placeholder

                const ctx = canvas.getContext('2d');
                const colors = labels.map((_, i) => SERIES[i % SERIES.length].color);

                if (referrersChart) referrersChart.destroy();

                referrersChart = new Chart(ctx, {
                    type: 'doughnut',
                    data: {
                        labels,
                        datasets: [{
                            data: values,
                            backgroundColor: colors,
                            borderColor: 'rgba(255,255,255,0.9)',
                            borderWidth: 2,
                            borderRadius: 3,
                            cutout: '55%'
                        }]
                    },
                    options: {
                        responsive: true,
                        maintainAspectRatio: false,
                        layout: { padding: 4 },
                        onResize: fitDonutLegend,
                        plugins: {
                            tooltip: {
                                callbacks: {
                                    label: (item) => {
                                        const value = item.parsed;
                                        const t = values.reduce((a,b)=>a+b,0);
                                        const pct = t ? ((value / t) * 100).toFixed(1) : 0;
                                        return `${item.label}: ${value} (${pct}%)`;
                                    }
                                }
                            },
                            legend: donutLegend(canvas),
                            datalabels: {
                                color: (context) => SERIES[context.dataIndex % SERIES.length].ink,
                                font: { weight: '600', size: 12 },
                                display: (context) => {
                                    const t = values.reduce((a,b)=>a+b,0);
                                    return t > 0 && (context.dataset.data[context.dataIndex] / t) >= 0.05; // hide labels on hairline slices
                                },
                                formatter: (val, ctx) => {
                                    const t = values.reduce((a,b)=>a+b,0);
                                    const pct = t ? ((val / t) * 100).toFixed(0) : 0;
                                    return `${pct}%`;
                                }
                            }
                        }
                    },
                    plugins: [ChartDataLabels]
                });
            } catch (e) {
                if (metaEl) metaEl.textContent = 'Error';
                console.error('Bitly referrers period load failed:', e);
            }
        }

        const linkSelect = bitlyReferrersPeriodWidget.querySelector('.bitly-link-select');
        const periodSelect = bitlyReferrersPeriodWidget.querySelector('.bitly-period-select');

        loadBitlyReferrersPeriod(bitlyReferrersPeriodWidget, linkSelect.value, periodSelect.value);
        linkSelect.addEventListener('change', () => {
            loadBitlyReferrersPeriod(bitlyReferrersPeriodWidget, linkSelect.value, periodSelect.value);

            const url = new URL(window.location);
            url.searchParams.delete('bitly-link');
            window.history.pushState({}, '', url);
        });
        periodSelect.addEventListener('change', () => loadBitlyReferrersPeriod(bitlyReferrersPeriodWidget, linkSelect.value, periodSelect.value));
    }
}
